import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Highlighter from 'react-highlight-words';
import Tooltip from '../molecules/Tooltip';
import OverflowingText from '../foundations/OverflowingText';
import css from './TableCellImgLabel.css';
import NoLogoIcon from '../../resources/svg/no_logo.svg';
import Avatar from '../../resources/svg/avatar_small.svg';

const TableCellImgLabel = ({
    tdStyle,
    tdClassName,
    markCell,
    markCellTooltipText,
    containerStyle,
    imgSrc,
    imgStyle,
    imgClassName,
    SideImg,
    sideImgHoverText,
    labelsStyle = {},
    labelsClassName,
    searchText,
    value,
    valueSuffix,
    valueClassName,
    valueSuffixClassName,
    subTextStyle,
    subTextClassName,
    subText,
    subTextExtraStyle,
    subTextExtraClassName,
    subTextExtra,
    showImgBadge,
    Icon,
    useDefaultEmptyImg = true,
    ImgComponent,
    reverseOrder = false,
    subTextElements = [],
    showUsernameImg,
    titleClassName,
    useOverflowingTextForValue = false,
}) => (
    <td style={tdStyle} className={tdClassName}>
        {markCell && (
            <Tooltip
                open={markCellTooltipText ? null : false}
                placement="top"
                title={markCellTooltipText}
                classes={{ tooltip: css.tooltip, popper: css.opaqueBackground }}
            >
                <div className={css.markCell} />
            </Tooltip>
        )}
        <div className={classNames([css.container, { [css.reversedContainer]: reverseOrder }])} style={containerStyle}>
            {showImgBadge && <Icon className={classNames('tableCellImgBadge', css.imgBadge)} />}
            {(imgSrc || ImgComponent || useDefaultEmptyImg) && (
                <div className={css.image}>
                    {imgSrc ? (
                        <img alt="" className={imgClassName} style={imgStyle} src={imgSrc} />
                    ) : ImgComponent ? (
                        <ImgComponent alt="" className={imgClassName} style={imgStyle} />
                    ) : (
                        <NoLogoIcon style={imgStyle} className={imgClassName} />
                    )}
                </div>
            )}
            <div className={classNames([css.labels, labelsClassName])} style={labelsStyle}>
                {(() => {
                    const fullValue = value || '';
                    const suffixValue = valueSuffix || '';
                    const baseValue =
                        suffixValue && fullValue.endsWith(suffixValue)
                            ? fullValue.slice(0, fullValue.length - suffixValue.length)
                            : fullValue;
                    const ValueContainer = useOverflowingTextForValue ? OverflowingText : 'div';
                    const valueContainerProps = {
                        title: `${fullValue}${suffixValue}`,
                        className: classNames(css.valueRow, titleClassName),
                    };

                    const valueTextClassName = useOverflowingTextForValue
                        ? css.ellipsisNoClamp
                        : css.ellipsis;

                    return (
                        <ValueContainer {...valueContainerProps}>
                            <span className={classNames(css.valueText, valueClassName)}>
                                <Highlighter
                                    highlightClassName={css.highlighted}
                                    searchWords={[searchText]}
                                    textToHighlight={baseValue}
                                    autoEscape
                                    className={valueTextClassName}
                                />
                            </span>
                            {suffixValue ? (
                                <span className={classNames(css.valueSuffix, valueSuffixClassName)}>
                                    {suffixValue}
                                </span>
                            ) : null}
                        </ValueContainer>
                    );
                })()}
                <div className={css.subTextContainer}>
                    {showUsernameImg ? (
                        <span className={css.avatar}>
                            <Avatar />
                        </span>
                    ) : null}
                    <OverflowingText className={`${subText ? '' : css.noSubTextExtra} ${css.subTextContainer}`}>
                        <Highlighter
                            unhighlightStyle={subTextStyle}
                            highlightStyle={subTextStyle}
                            unhighlightClassName={subTextClassName}
                            highlightClassName={subTextClassName || css.highlighted}
                            searchWords={[searchText]}
                            textToHighlight={subText || ''}
                            autoEscape
                        />
                    </OverflowingText>
                </div>
                <div className={css.subTextContainer}>
                    {subTextElements}
                    <OverflowingText className={css.ellipsis}>
                        <Highlighter
                            unhighlightStyle={subTextExtraStyle}
                            highlightStyle={subTextExtraStyle}
                            unhighlightClassName={subTextExtraClassName}
                            highlightClassName={subTextExtraClassName || css.highlighted}
                            searchWords={[searchText]}
                            textToHighlight={subTextExtra || ''}
                            autoEscape
                        />
                    </OverflowingText>
                </div>
                {SideImg && sideImgHoverText && (
                    <div className={css.image}>
                        <Tooltip wrapperClass={css.sideImg} title={sideImgHoverText}>
                            <SideImg />
                        </Tooltip>
                    </div>
                )}
            </div>
        </div>
    </td>
);

TableCellImgLabel.propTypes = {
    imgStyle: PropTypes.object,
    containerStyle: PropTypes.object,
    imgSrc: PropTypes.string,
    searchWords: PropTypes.string,
    markCell: PropTypes.bool,
    bottomMarkCell: PropTypes.bool,
    useDefaultEmptyImg: PropTypes.bool,
    ImgComponent: PropTypes.object,
    reverseOrder: PropTypes.bool,
    labelsClassName: PropTypes.object,
    subTextExtraClassName: PropTypes.object,
    subTextElements: PropTypes.arrayOf(PropTypes.element),
    showUsernameImg: PropTypes.bool,
    valueSuffix: PropTypes.string,
    valueClassName: PropTypes.object,
    valueSuffixClassName: PropTypes.object,
    useOverflowingTextForValue: PropTypes.bool,
};

export default TableCellImgLabel;
